import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataLabel, fmtDate, daysUntil, urgencyClass } from "@/lib/utils-ui";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import ExportMenu from "@/components/ExportMenu";
import ConfirmDialog from "@/components/ConfirmDialog";

const TYPES = ["domain","ssl","software_license","cloud_service","other"];
const CYCLES = ["monthly","quarterly","annual","biannual"];

export default function SubscriptionsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState({ name: "", type: "domain", provider: "", expiry_date: "", cost: 0, currency: "USD", renewal_cycle: "annual", company_id: "" });

  const load = async () => {
    const { data } = await api.get("/subscriptions");
    setItems(data);
  };
  useEffect(() => { load(); api.get("/companies").then(r => setCompanies(r.data)); }, []);

  const create = async () => {
    try {
      const payload = { ...form, cost: parseFloat(form.cost) || 0 };
      if (user.role !== "super_admin") delete payload.company_id;
      if (editing) {
        await api.put(`/subscriptions/${editing.id}`, payload);
        toast.success("Subscription updated");
      } else {
        await api.post("/subscriptions", payload);
        toast.success("Subscription added");
      }
      setOpen(false); setEditing(null); load();
      setForm({ name: "", type: "domain", provider: "", expiry_date: "", cost: 0, currency: "USD", renewal_cycle: "annual", company_id: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const startEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name, type: s.type, provider: s.provider || "", expiry_date: s.expiry_date,
      cost: s.cost, currency: s.currency, renewal_cycle: s.renewal_cycle, company_id: s.company_id,
    });
    setOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/subscriptions/${deleting.id}`);
      toast.success("Subscription deleted");
      setDeleting(null); load();
    } catch (e) { toast.error("Failed"); }
  };

  const canEdit = ["super_admin","company_admin","it_admin"].includes(user?.role);

  // group by urgency
  const groups = { critical: [], warning: [], upcoming: [], ok: [] };
  items.forEach(s => {
    const d = daysUntil(s.expiry_date);
    if (d <= 7) groups.critical.push(s);
    else if (d <= 15) groups.warning.push(s);
    else if (d <= 30) groups.upcoming.push(s);
    else groups.ok.push(s);
  });

  return (
    <div className="p-8 space-y-6" data-testid="subscriptions-page">
      <div className="flex items-start justify-between">
        <div>
          <DataLabel>Module 02</DataLabel>
          <h1 className="font-head font-black uppercase text-4xl tracking-tighter mt-1">Subscriptions</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">Domains · SSL · Licenses · Cloud services. Never miss a renewal.</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <ExportMenu module="subscriptions" />
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild><Button data-testid="new-sub-btn" onClick={() => { setEditing(null); setForm({ name: "", type: "domain", provider: "", expiry_date: "", cost: 0, currency: "USD", renewal_cycle: "annual", company_id: "" }); }} className="bg-black text-white hover:bg-[#FF5000] h-10 uppercase tracking-wider text-xs font-head"><Plus size={14} className="mr-1"/>New</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-head uppercase tracking-tight">{editing ? "Edit Subscription" : "Add Subscription"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                {user.role === "super_admin" && (
                  <div className="col-span-2">
                    <Label className="data-label">Company</Label>
                    <Select value={form.company_id} onValueChange={(v) => setForm({...form, company_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Select"/></SelectTrigger>
                      <SelectContent>{companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div className="col-span-2"><Label className="data-label">Name</Label><Input data-testid="sub-form-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})}/></div>
                <div><Label className="data-label">Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({...form, type: v})}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="data-label">Cycle</Label>
                  <Select value={form.renewal_cycle} onValueChange={(v) => setForm({...form, renewal_cycle: v})}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>{CYCLES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="data-label">Provider</Label><Input value={form.provider} onChange={e => setForm({...form, provider: e.target.value})}/></div>
                <div><Label className="data-label">Expiry</Label><Input data-testid="sub-form-expiry" type="date" value={form.expiry_date} onChange={e => setForm({...form, expiry_date: e.target.value})}/></div>
                <div><Label className="data-label">Cost</Label><Input type="number" value={form.cost} onChange={e => setForm({...form, cost: e.target.value})}/></div>
                <div><Label className="data-label">Currency</Label><Input value={form.currency} onChange={e => setForm({...form, currency: e.target.value})}/></div>
              </div>
              <DialogFooter><Button data-testid="sub-form-submit" onClick={create} className="bg-black text-white hover:bg-[#FF5000]">{editing ? "Save" : "Create"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      <ConfirmDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}
        title="Delete subscription?" description={deleting?.name} confirmLabel="Delete" variant="destructive"
        onConfirm={confirmDelete} testid="delete-sub" />

      {/* Urgency bands */}
      <div className="grid grid-cols-4 gap-0 border border-border bg-white">
        {[["critical","≤ 7 DAYS","urgency-7"],["warning","≤ 15 DAYS","urgency-15"],["upcoming","≤ 30 DAYS","urgency-30"],["ok","HEALTHY","urgency-ok"]].map(([k,l,cls]) => (
          <div key={k} className="p-4 border-r border-border last:border-r-0" data-testid={`urgency-band-${k}`}>
            <div className={`data-label ${cls}`}>{l}</div>
            <div className="font-head font-black text-3xl tracking-tighter mt-1">{groups[k].length}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="border border-border bg-white overflow-x-auto" data-testid="sub-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted">
              {["NAME","TYPE","PROVIDER","EXPIRY","DAYS","COST","CYCLE"].map(h => <th key={h} className="text-left p-3 data-label">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {items.map(s => {
              const d = daysUntil(s.expiry_date);
              return (
                <tr key={s.id} className="border-b border-border hover-row" data-testid={`sub-row-${s.id}`}>
                  <td className="p-3 font-semibold">{s.name}</td>
                  <td className="p-3 uppercase text-xs">{s.type.replace("_"," ")}</td>
                  <td className="p-3 text-xs">{s.provider || "—"}</td>
                  <td className="p-3 font-mono text-xs">{fmtDate(s.expiry_date)}</td>
                  <td className="p-3"><span className={`data-label ${urgencyClass(d)}`}>{d} D</span></td>
                  <td className="p-3 font-mono text-xs">{s.currency} {s.cost.toFixed(2)}</td>
                  <td className="p-3 uppercase text-xs">{s.renewal_cycle}</td>
                  <td className="p-3">
                    {canEdit && (
                      <div className="flex items-center gap-3">
                        <button data-testid={`edit-sub-${s.id}`} onClick={() => startEdit(s)} title="Edit" className="hover:text-[#FF5000]"><Pencil size={14}/></button>
                        <button data-testid={`delete-sub-${s.id}`} onClick={() => setDeleting(s)} title="Delete" className="hover:text-[#FF3B30]"><Trash2 size={14}/></button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={8} className="p-10 text-center text-muted-foreground">No subscriptions.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
